/**
 * S8 · Order confirmed (design files 8 / 8w).
 *
 * Terminal receipt for the consumer funnel. Everything shown here is read from
 * the real submitted card_request — no money figures are invented, so the
 * design's price column only renders when the row actually carries an amount.
 *
 * LEGAL GATES (L1): no scheme name, no patent claim, no present-tense issuance.
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getUserLatestCardRequest } from "@/services/card-requests";
import { readSelectedVariant, readStylePick } from "@/lib/consumer-session";
import { toCssColour } from "@/lib/color";
import { readDesignedCard } from "@/lib/designed-card";
import { DesignedCardFace } from "@/components/consumer/DesignedCardFace";
import { LAUNCH_PROGRAM_SLUG, getPublicProgramBySlug, type PublicProgramStyle } from "@/services/programs";
import { repeatEntryPath, postalAddressSummary } from "@/lib/repeat-flow";
import { findTile, tileColor } from "@/lib/style-art";
import {
  Display,
  FunnelContainer,
  FunnelHeader,
  FunnelPage,
  Lede,
  PrimaryButton,
  SectionTitle,
  SentenceLink,
  StickyBar,
} from "@/components/consumer/chrome";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S8_SECTION, S8_DEFAULTS } from "@/content/screens/s8";

interface OrderRow {
  ref_number: string | null;
  status: string | null;
  style: string | null;
  card_color: string | null;
  delivery_type: string | null;
  postal_address: any;
}

function addressLine(row: OrderRow | null, yourBranchLabel: string): string | null {
  if (!row) return null;
  if (row.delivery_type === "branch" || row.delivery_type === "in_branch") return yourBranchLabel;
  // ONE resolver — canonical `region`/`postcode` plus legacy fallbacks.
  return postalAddressSummary(row.postal_address as Record<string, string> | null);
}

export default function S8Confirmed() {
  const navigate = useNavigate();
  const location = useLocation();
  const stateRef = (location.state as { ref?: string } | null)?.ref ?? null;
  const { t } = useSiteContent(S8_SECTION, S8_DEFAULTS);

  const [row, setRow] = useState<OrderRow | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  /**
   * N15a · the load ceiling is a REGULATORY figure that varies by program, so it
   * is read from program settings (`public_config.maxLoadCents`). There is no
   * fallback: with no configured value the line is not rendered at all.
   */
  const [maxLoad, setMaxLoad] = useState<string | null>(null);
  const [programTiles, setProgramTiles] = useState<PublicProgramStyle[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!auth?.user) {
        setLoading(false);
        return;
      }
      setEmail(auth.user.email ?? null);
      const [{ data }, programRes] = await Promise.all([
        getUserLatestCardRequest(auth.user.id),
        getPublicProgramBySlug(LAUNCH_PROGRAM_SLUG),
      ]);
      if (cancelled) return;
      setRow((data as unknown as OrderRow) ?? null);
      setProgramTiles(programRes.data?.styles ?? []);
      const cents = Number((programRes.data?.config as any)?.maxLoadCents);
      setMaxLoad(
        Number.isFinite(cents) && cents > 0
          ? `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`
          : null,
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refNumber = row?.ref_number ?? stateRef;
  const status = row?.status ?? "pending";
  const STATUS_COPY: Record<string, string> = {
    pending: t("s8.status.pending"),
    submitted: t("s8.status.pending"),
    waitlisted: t("s8.status.waitlisted"),
    approved: t("s8.status.approved"),
    fulfilled: t("s8.status.approved"),
    rejected: t("s8.status.rejected"),
  };
  const statusLabel = STATUS_COPY[status] ?? t("s8.status.fallback");
  const pending = status === "pending" || status === "submitted" || status === "waitlisted";

  const variant = readSelectedVariant(LAUNCH_PROGRAM_SLUG);
  const pick = readStylePick(LAUNCH_PROGRAM_SLUG);
  const styleKey = row?.style || pick?.style_id || null;
  const art = findTile(programTiles, styleKey);
  const cardColor = toCssColour(row?.card_color) || tileColor(programTiles, styleKey);
  // THE designed card; the persisted order colour still wins if present.
  const designed = readDesignedCard(LAUNCH_PROGRAM_SLUG);
  const designedCard = { ...designed, colour: toCssColour(row?.card_color) || designed.colour };
  const styleName = art?.name || styleKey || "Your card";
  const posted = addressLine(row, "your branch");

  async function copyRef() {
    if (!refNumber) return;
    try {
      await navigator.clipboard.writeText(refNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <FunnelPage testId="s8-confirmed" pb="pb-10">
      <FunnelContainer>
        <FunnelHeader
          backTo="/"
          backLabel="Back to home"
          right={(
            <Link
              to="/account"
              className="flex min-h-11 items-center text-[14px] font-semibold text-grape hover:text-tangerine"
              data-testid="s8-account-link"
            >
              {t("s8.header.accountLink")}
            </Link>
          )}
        />

        <div className="mb-[18px] flex justify-center">
          <div className="grid h-[68px] w-[68px] animate-[cmPop_520ms_cubic-bezier(0.22,1,0.36,1)_both] place-items-center rounded-full bg-volt">
            <svg viewBox="0 0 24 24" className="h-[34px] w-[34px]">
              <path d="M5 12.6l4.2 4.2L19 7" fill="none" stroke="#1C2030" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <Display className="mb-3 text-center">{t("s8.title")}</Display>
        <Lede className="mx-auto mb-6 max-w-[40ch] text-center">
          {t("s8.lede")}
        </Lede>

        {/*
          N15 · restructured. The "Approved" status panel is GONE (it read as a
          decision that has not happened), the not-yet-charged paragraph is gone,
          and the order number now sits in the order panel itself.
        */}
        <div className="mb-[26px] rounded-[20px] border border-foreground/10 bg-white p-[18px]">
          <div className="flex items-center gap-3.5">
            <div className={designedCard.orientation === "landscape" ? "w-[76px] flex-none" : "w-12 flex-none"}>
              <DesignedCardFace
                card={designedCard}
                fallbackArt={art?.art ?? null}
                alt={styleName}
                data-testid="s8-card"
                className="rounded-lg"
              />
            </div>
            <div className="flex min-w-0 flex-col gap-[3px]">
              <span className="text-[16px] font-semibold">{styleName}</span>
              {refNumber ? (
                <button
                  type="button"
                  onClick={copyRef}
                  className="cursor-pointer border-0 bg-transparent p-0 text-left text-[13px] text-foreground/70 hover:text-ink"
                  data-testid="s8-ref"
                >
                  {copied ? t("s8.ref.copied") : `${t("s8.ref.orderPrefix")}${refNumber}`}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* What happens next — four steps, counsel-approved wording only. */}
        <SectionTitle className="mb-3.5">{t("s8.timeline.title")}</SectionTitle>
        <div className="mb-[26px] flex flex-col">
          <TimelineStep
            state="done"
            title={t("s8.timeline.receivedTitle")}
            body={email ? <>{t("s8.timeline.receivedEmailSuffix")}{email}</> : null}
          />
          {/* Both bodies are EMPTY today, so nothing extra is shown — but they
              are rendered when they hold text, because a counsel-owned key that
              can be edited and can never appear is the same trap as wording
              reviewed for a screen that does not exist. */}
          <TimelineStep
            state="active"
            title={t("s8.timeline.decisionTitle")}
            body={t("s8.timeline.decisionBody")?.trim() ? <>{t("s8.timeline.decisionBody")}</> : null}
          />
          <TimelineStep
            state="todo"
            title={t("s8.timeline.productionTitle")}
            body={t("s8.timeline.productionBody")?.trim() ? <>{t("s8.timeline.productionBody")}</> : null}
          />
          <TimelineStep
            state="todo"
            last
            title={t("s8.timeline.postTitle")}
            body={
              maxLoad ? (
                <>{t("s8.timeline.postBody").replace("{limit}", maxLoad)}</>
              ) : null
            }
          />
        </div>

        <p className="mt-5 text-center text-[13px] leading-[1.55] text-foreground/70">
          {t("s8.support.question")}{" "}
          <a href="mailto:support@cardmoji.com" className="text-azure hover:text-tangerine">
            {t("s8.support.link")}
          </a>
          {refNumber ? <>{t("s8.support.withOrder").replace("{ref}", refNumber)}</> : t("s8.support.period")}
        </p>

        {loading ? <span className="sr-only">{t("s8.loading")}</span> : null}
      </FunnelContainer>

      {/* N16 · ONE pinned primary CTA. The secondary is a sentence link below it. */}
      <StickyBar>
        <PrimaryButton onClick={() => navigate("/account")} data-testid="s8-account-cta" className="basis-full">
          {t("s8.actions.account")}
        </PrimaryButton>
        <span className="basis-full text-center">
          <SentenceLink onClick={() => navigate(repeatEntryPath(LAUNCH_PROGRAM_SLUG))} data-testid="s8-add-another">
            {t("s8.actions.addAnother")}
          </SentenceLink>
        </span>
      </StickyBar>
    </FunnelPage>
  );
}

function TimelineStep({
  state,
  title,
  body,
  last,
}: {
  state: "done" | "active" | "todo";
  title: string;
  body: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div className="flex gap-3.5">
      <div className="flex flex-none flex-col items-center gap-1">
        {state === "done" ? (
          <span className="grid h-[26px] w-[26px] place-items-center rounded-full bg-leaf">
            <svg viewBox="0 0 20 20" className="h-[13px] w-[13px]">
              <path d="M5.4 10.4l3 3 6.2-6.6" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        ) : state === "active" ? (
          <span className="grid h-[26px] w-[26px] place-items-center rounded-full bg-amber">
            <span className="h-[9px] w-[9px] rounded-full bg-ink" />
          </span>
        ) : (
          <span className="h-[26px] w-[26px] rounded-full border-2 border-foreground/20 bg-paper" />
        )}
        {!last ? <span className="w-0.5 flex-1 bg-foreground/[0.14]" /> : null}
      </div>
      <div className={last ? "" : "pb-5"}>
        <span className={"text-[16px] font-semibold" + (state === "todo" ? " text-foreground/75" : "")}>
          {title}
        </span>
        <p className="m-0 mt-[3px] text-[14px] leading-[1.5] text-foreground/70">{body}</p>
      </div>
    </div>
  );
}
